/**
 * Which brand the portal presents as.
 *
 * One deployment serves several hostnames — `portal.the-real-insight.ai` and
 * `portal.fireiq.de` are the same origin behind a CNAME, not a redirect — so the
 * brand cannot be baked in at build time the way tri-app does it with
 * `APP_VARIANT` (see tri-app `app.config.ts` / `app/shared/brand/brand.ts`).
 * It is resolved at runtime from `window.location.hostname` instead, and the
 * `tri` entry is kept identical to the previous hardcoded presentation so the
 * existing portal is unaffected.
 *
 * The brand is *pinned*: on a FireIQ host the portal shows FireIQ before and
 * after login, whichever tenant the signed-in user belongs to. That mirrors the
 * decision taken for the FireIQ app.
 *
 * ⚠️ `public/index.html` carries a copy of `HOSTS` in its inline boot script —
 * the document title, favicon and manifest have to be right before React runs.
 * `scripts/brand-shells.js` and the `nginx.*.conf` host map carry it a third
 * and fourth time: link previews run no script, so the build stamps a branded
 * copy of index.html per brand and nginx serves it by Host. `brand.test.ts`
 * fails if any of the lists drift apart.
 */

export type BrandKey = 'tri' | 'fireiq';

export interface Brand {
  key: BrandKey;
  /** Display name: sidebar wordmark, image `alt`, i18n `{{brandName}}`. */
  name: string;
  /** What the phone app is called in running text ("TRI-App", "FireIQ-App"): i18n `{{appName}}`. */
  appName: string;
  /** The mark of the brand's phone app (its home-screen icon): shown on the push preview's lock screen. */
  appIcon: string;
  /** The portal address of the brand's own host: the sample `link` of the alert preview. */
  portalRootURL: string;
  /** Wordmark for light surfaces — sidebar, header, and the pages on `bg-background`. */
  logo: string;
  /**
   * Wordmark for a surface flooded with `loginBackgroundColor`: the dark-ground
   * variant of the same mark. `null` when the brand paints no such surface.
   */
  logoOnBrand: string | null;
  /**
   * When set, the login page floods this color instead of `bg-background`, the
   * way the FireIQ app's login screen does.
   */
  loginBackgroundColor: string | null;
  /**
   * True when the wordmark already spells the brand out, so the sidebar and
   * header must not repeat the name in text beside it — and the mark gets the
   * room that text would have taken.
   */
  logoIncludesName: boolean;
  /**
   * Offer "Sign in with Microsoft" on the first sign-in screen, before the
   * email is known. The server resolves the account from the Entra tenant, so
   * no email is needed; a customer without Entra gets the server's refusal.
   * Off for FireIQ, matching the app's `BRAND.entraSsoEnabled`.
   */
  entraSsoEnabled: boolean;
  /**
   * Offer "Scoring heatmap" in the settings sidebar. Off for FireIQ: the page belongs to the site and
   * location analysis of The Real Insight, not to the fire protection product. It only hides the entry;
   * the panel itself stays reachable by its address, like every other sidebar choice.
   */
  scoringHeatmapEnabled: boolean;
  /**
   * Colors of the sign-in and sign-up pages while no tenant theme is known
   * (no `?tenant=`, nobody signed in yet). Scoped to those pages; the portal
   * behind the login keeps `accentColor`. Once the signed-in tenant's theme
   * arrives, the page morphs over to it. `null` ⇒ the pages use `accentColor`.
   */
  loginTheme: { primary: string } | null;
  /**
   * Square mark for the places that render a fixed-size tile: the OAuth bridge
   * and the collapsed sidebar. A brand whose wordmark is a wide lockup needs a
   * real square mark here — cropping the wordmark to a square yields a letter
   * fragment, not a logo.
   */
  squareLogo: string;
  /** Rendered instead of the mark when the image fails to load. */
  fallbackInitials: string;
  /** Primary/accent color; seeds the theme when no tenant theme overrides it. */
  accentColor: string;
  /**
   * Set ⇒ the brand is pinned: no tenant theme and no `?tenant=` may repaint
   * it, and `accentColor` above stands in for the tenant's colors. The value is
   * the tenant the FireIQ app pins, kept here so both codebases name the same
   * one. `null` ⇒ follow the signed-in tenant (the portal's existing
   * behaviour).
   */
  defaultTenant: string | null;
}

/**
 * Hostnames that select a brand. Anything not listed falls back to `tri`, so a
 * new domain shows The Real Insight until it is added here deliberately.
 * fireiq.de and www.fireiq.de are the marketing site, not this portal.
 * Keep in sync with the inline boot script in `public/index.html`.
 */
export const HOSTS: Record<string, BrandKey> = {
  'portal.fireiq.de': 'fireiq',
};

export const BRANDS: Record<BrandKey, Brand> = {
  tri: {
    key: 'tri',
    name: 'The Real Insight',
    appName: 'TRI',
    appIcon: '/logo.png',
    portalRootURL: 'https://www.the-real-insight.ai',
    logo: '/logo.png',
    logoOnBrand: null,
    loginBackgroundColor: null,
    // The TRI mark is a monogram; the name beside it is what names the portal.
    logoIncludesName: false,
    entraSsoEnabled: true,
    scoringHeatmapEnabled: true,
    // The palette of The Real Insight's own tenant (S8QW-G8R2-9QLC).
    loginTheme: { primary: '#c3a9d6' },
    squareLogo: '/logo.png',
    fallbackInitials: 'RI',
    accentColor: '#000000',
    defaultTenant: null,
  },
  fireiq: {
    key: 'fireiq',
    name: 'FireIQ',
    appName: 'FireIQ',
    appIcon: '/brands/fireiq/app-icon.png',
    portalRootURL: 'https://portal.fireiq.de',
    logo: '/brands/fireiq/logo-dark.svg',
    logoOnBrand: '/brands/fireiq/logo-light.svg',
    loginBackgroundColor: '#006650',
    logoIncludesName: true,
    entraSsoEnabled: false,
    scoringHeatmapEnabled: false,
    loginTheme: null,
    // The mark fireiq.de itself uses as its favicon.
    squareLogo: '/brands/fireiq/icon.png',
    fallbackInitials: 'FQ',
    accentColor: '#085b48',
    // Same tenant the FireIQ app pins (tri-app app.config.ts).
    defaultTenant: 'XI4D-CFWT-7JBS',
  },
};

const isBrandKey = (value: string | null | undefined): value is BrandKey =>
  value === 'tri' || value === 'fireiq';

/**
 * `?brand=fireiq` and `REACT_APP_BRAND` exist so the FireIQ presentation can be
 * opened on localhost and on the real portal, where the hostname says something
 * else. Neither is a security boundary — the brand decides presentation only,
 * never what data a user may see or what the API answers.
 */
export const resolveBrandKey = (
  hostname: string | null | undefined,
  brandOverride?: string | null,
): BrandKey => {
  if (isBrandKey(brandOverride)) {
    return brandOverride;
  }

  return HOSTS[(hostname ?? '').toLowerCase()] ?? 'tri';
};

/**
 * Where `?brand=` is remembered. Session storage, so the override survives
 * clicking through the portal and reloading, and is gone once the tab closes —
 * a test drive can't outlive the tab it was started in.
 *
 * `?brand=reset` (or `off`) drops it and hands the decision back to the host.
 * `public/index.html` reads and writes the same key for the document title,
 * the favicon and the manifest.
 */
export const OVERRIDE_STORAGE_KEY = 'tri.brand.override';

/**
 * The override for this load, given the query string and the tab's storage.
 * Reads *and* writes: a `?brand=` in the URL is what puts it in storage.
 *
 * Storage that throws (private mode, blocked site data) is not an error here —
 * the URL parameter still brands the load it appears on, it just isn't carried
 * to the next one.
 */
export const resolveOverride = (
  search: string,
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null,
): string | null => {
  const fromUrl = new URLSearchParams(search).get('brand');
  const clearing = fromUrl === 'reset' || fromUrl === 'off';

  try {
    if (clearing) {
      storage?.removeItem(OVERRIDE_STORAGE_KEY);
    } else if (fromUrl) {
      storage?.setItem(OVERRIDE_STORAGE_KEY, fromUrl);
    } else {
      return storage?.getItem(OVERRIDE_STORAGE_KEY) ?? null;
    }
  } catch {
    // Fall through to whatever the URL said.
  }

  return clearing ? null : fromUrl;
};

const readOverride = (): string | null => {
  if (typeof window === 'undefined') {
    return process.env.REACT_APP_BRAND ?? null;
  }

  let storage: Storage | null = null;
  try {
    storage = window.sessionStorage;
  } catch {
    storage = null;
  }

  return (
    resolveOverride(window.location.search, storage) ??
    process.env.REACT_APP_BRAND ??
    null
  );
};

export const BRAND: Brand =
  BRANDS[
    resolveBrandKey(
      typeof window === 'undefined' ? null : window.location.hostname,
      readOverride(),
    )
  ];
